import { withStyles, Grid, Typography, Paper } from '@material-ui/core';
import React from 'react'; // eslint-disable-line
import styles, { StyledComponent } from '../../../../../styles/styles';
import { LabeledValue } from '../../../../../controls/labeled-value';
import { ExpansionPanel } from '../../../../../forms/form-components/expansion-panel/expansion-panel';
import { FormDetails, getFormDetails, DoctorVisit, Occurence } from '../form-details-reducer';
import { connect } from 'react-redux';
import { State } from '../../../../../redux/root-reducer';
import { getDisplayDate } from '../../../../../shared-functions';

interface BaseProps {
    formDetails?: FormDetails;
    disableEditButton?: boolean;
}

interface Props extends BaseProps, StyledComponent {}

function formatDoctorAddress(addressDetails: DoctorVisit['addressDetails']) {
    if (!addressDetails) {
        return null;
    }
    return [
        addressDetails.address,
        addressDetails.suburb,
        addressDetails.city,
        addressDetails.postalCode,
        addressDetails.province,
        addressDetails.country,
    ]
        .filter(Boolean)
        .join('\n');
}

const medicalInformationDetailsView: React.FunctionComponent<Props> = ({
    classes,
    formDetails,
}: Props) => {
    const medicalInformation = formDetails?.lifeAssured?.incomeProtectorClaimConditions;
    const doctorVisits = medicalInformation?.doctorVisits || [];
    const isAccident = medicalInformation?.medicalConditionWasAcquiredDueTo === 'Accident';

    return (
        <React.Fragment>
            <Grid container justify="space-between" className={classes.marginTop2}>
                <Grid container item xs={10} alignItems="center">
                    <Typography style={{ padding: 10 }} className={classes.heading} variant="body1">
                        Medical Information
                    </Typography>
                </Grid>
            </Grid>
            <Paper className={classes.paper}>
                <Grid container justify="flex-start">
                    <Grid item xs={12} container justify="flex-start">
                        <Grid item xs={4} lg={3}>
                            <LabeledValue
                                label="What is the medical reason for the claim being submitted?"
                                value={
                                    medicalInformation?.whatIsTheMedicalReasonForTheClaimBeingSubmitted
                                }
                            />
                        </Grid>
                        <Grid item xs={4} lg={3}>
                            <LabeledValue
                                label="Please provide the date of diagnosis or date that the event took place"
                                value={getDisplayDate(
                                    medicalInformation?.pleaseProvideTheDateOfDiagnosisOrDateThatTheEventTookPlace
                                )}
                            />
                        </Grid>
                        <Grid item xs={4} lg={3}>
                            <LabeledValue
                                label="Medical condition was acquired due to?"
                                value={medicalInformation?.medicalConditionWasAcquiredDueTo}
                            />
                        </Grid>
                        {isAccident && (
                            <Grid item xs={4} lg={3}>
                                <LabeledValue
                                    label="Please provide details of the accident, including the case number and the name of the police station"
                                    value={
                                        medicalInformation?.pleaseProvideDetailsOfTheAccidentIncludingTheCaseNumberAndTheNameOfThePoliceStation
                                    }
                                />
                            </Grid>
                        )}
                    </Grid>
                </Grid>
            </Paper>
            {doctorVisits.map((doctor: DoctorVisit, doctorIndex: number) => (
                <ExpansionPanel key={doctorIndex} name={`Doctor ${doctorIndex + 1}`}>
                    <Grid container justify="flex-start">
                        <Grid item xs={12} container justify="flex-start">
                            <Grid item xs={4} lg={3}>
                                <LabeledValue label="Dr name" value={doctor.doctorName} />
                            </Grid>
                            <Grid item xs={4} lg={3}>
                                <LabeledValue
                                    label="Dr's contact number"
                                    value={doctor.doctorContactNumber}
                                />
                            </Grid>
                            <Grid item xs={4} lg={3}>
                                <LabeledValue
                                    label="Address"
                                    value={formatDoctorAddress(doctor.addressDetails)}
                                />
                            </Grid>
                            <Grid item xs={4} lg={3}>
                                <LabeledValue
                                    label="What form of treatment or medication or treatment are you currently taking?"
                                    value={
                                        medicalInformation?.whatFormOfTreatmentOrMedicationAreYouCurrentlyTaking
                                    }
                                />
                            </Grid>
                        </Grid>
                        {Array.isArray(doctor.occurences) &&
                            doctor.occurences.map((occurence: Occurence, visitIndex: number) => (
                                <Grid item xs={12} key={visitIndex}>
                                    <ExpansionPanel
                                        name={`reasonForConsultation${visitIndex + 1}`}
                                        label={`Reason for Consultation ${visitIndex + 1}`}
                                        style={{ marginLeft: -16, marginRight: -16 }}
                                    >
                                        <Grid container justify="flex-start">
                                            <Grid item xs={4} lg={3}>
                                                <LabeledValue
                                                    label="Consultation date"
                                                    value={getDisplayDate(
                                                        occurence.consultationDate
                                                    )}
                                                />
                                            </Grid>
                                            <Grid item xs={4} lg={3}>
                                                <LabeledValue
                                                    label="Please provide details of the consultation"
                                                    value={
                                                        occurence.pleaseProvideDetailsOfTheConsultation
                                                    }
                                                />
                                            </Grid>
                                        </Grid>
                                    </ExpansionPanel>
                                </Grid>
                            ))}
                    </Grid>
                </ExpansionPanel>
            ))}
        </React.Fragment>
    );
};

const mapStateToProps = (state: State, ownProps: BaseProps) => ({
    formDetails: ownProps.formDetails || getFormDetails(state),
    disableEditButton: ownProps.disableEditButton || false,
});

export const MedicalInformationDetailsView = connect(mapStateToProps)(
    withStyles(styles)(medicalInformationDetailsView)
);
